import { createSlice } from '@reduxjs/toolkit';
import LoadingStatus from 'enums/loadingStatus';
import initialState from './initialState';
import {
  filterPlansFromBuildablePlans,
  getAllBuildablePlans, 
  getMarkers,
  updateMarkersWithSitePlans,
  updatePageNumberOfMarkers
} from '../services/processMarkers';

/* Creating a slice of the state for stuff related to the map. */
export const mapStateSlice = createSlice({
  name: 'mapState',
  initialState,
  reducers: {
    enableSearchWithinMap: (state) => {
      state.isSearchWithinMap = true;
      state.isSearchWithinMapReady = false;
    },
    disableSearchWithinMap: (state) => {
      state.isSearchWithinMap = false;
      state.isSearchWithinMapReady = false;
    },
    fetchResults: (state, action) => {
      state.isFetchingResults = action.payload.isFetchingResults;
      state.selectedMarker = null;
      state.nearbyListingsCarousel = null;
    },
    fetchResultsError: (state) => {
      state.isFetchingResults = false;
    },
    fetchResultsFulfilled: (state, action) => {
      const distinctHousingDevelopments = action.payload?.communities.CommunityResults || [];
      const productType = action.payload.parameters.product.productType;

      const allHousing = {
        communities: distinctHousingDevelopments,
        buildablePlans: [],
        specs: [],
        plans: [],
      };

      if (productType === 'homes') {
        const results = action.payload?.interactiveSitePlanHomes || action.payload?.homes;

        const plans = results?.Plans || [];
        allHousing.buildablePlans = getAllBuildablePlans(distinctHousingDevelopments, plans);
        allHousing.plans = filterPlansFromBuildablePlans(plans, allHousing.buildablePlans);
        allHousing.specs = results?.Specs || [];

        state.allPlanIds = plans.map((plan) => plan.Id);
      }

      state.isFetchingResults = false;
      state.results = getMarkers(productType, allHousing);
    },
    fetchResultsPagesNumbers: (state, action) => {
      const distinctHousingDevelopments = action.payload?.communities.CommunityResults || [];
      const productType = action.payload.parameters.product.productType;
      const allHousing = {
        communities: distinctHousingDevelopments,
        buildablePlans: [],
        specs: [],
        plans: [],
      };

      updatePageNumberOfMarkers(productType, allHousing, state.results);
    },
    updateSitePlanMarkers: (state, action) => {
      updateMarkersWithSitePlans(state.results, action.payload);
    },
    mapLibraryFetch: (state) => {
      state.mapLibraryStatus = LoadingStatus.Loading.toString();
    },
    mapLibraryLoaded: (state) => {
      state.mapLibraryStatus = LoadingStatus.Succeeded.toString();
    },
    mapLibraryErrored: (state) => {
      state.mapLibraryStatus = LoadingStatus.Failed.toString();
    },
    onSearchWithinMapReady: (state) => {
      state.isSearchWithinMapReady = true;
    },
    selectedMarker: (state, action) => {
      state.selectedMarker = action.payload;
      state.nearbyListingsCarouselStatus = LoadingStatus.Loading.toString();
    },
    /*
      The 'Update coordinates' action is intended to be changed exclusively by the map itself.
      It is imperative to avoid manually modifying the 'geoCoordinates' property, as doing so would compromise
      the intended functionality of the property and potentially lead to unintended adverse effects.
    */
    updateCoordinates: (state, action) => {
      state.geoCoordinates = action.payload;
    },
    getNearbyListingsCarouselFulfilled: (state, action) => {
      state.nearbyListingsCarouselStatus = LoadingStatus.Succeeded.toString();
      state.nearbyListingsCarousel = action.payload;
    },
    getNearbyListingsCarouselFailed: (state) => {
      state.nearbyListingsCarouselStatus = LoadingStatus.Failed.toString();
      state.nearbyListingsCarousel = null;
    },
    setActiveNearbyListingsCarousel: (state, action) => {
      state.nearbyListingsCarouselStatus = LoadingStatus.Succeeded.toString();
      state.nearbyListingsCarousel = action.payload;   
    },
    clearNearbyListingsCarousel: (state) => {
      state.nearbyListingsCarouselStatus = LoadingStatus.Idle.toString();
      state.nearbyListingsCarousel = null;
      state.selectedMarker = null;
    },
    updateMapZoomLevel: (state, action) => {
      state.zoomLevel = action.payload;
    },
    addSitePlan: (state, action) => {
      state.sitePlans.push(action.payload);
    },
    removeSitePlan: (state, action) => {
      state.sitePlans = state.sitePlans.filter(
        sitePlan =>
          sitePlan.communityId !== action.payload.communityId &&
          sitePlan.sitePlanId !== action.payload.sitePlanId
      );
    },
    updateInteractiveSitePlanLoadingStatus: (state, action) => {
      action.payload.sitePlanIds.forEach(sitePlanId => {
        const sitePlan = state.sitePlans.find(plan => plan.sitePlanId === sitePlanId);
        sitePlan.markersLoadingStatus = action.payload.loadingStatus;  
      });
    },
  },
});

export const interactiveSitePlanSlice = createSlice({
  name: 'interactiveSitePlanState',
  initialState: {
    loadedInteractiveSitePlans: [],
    interactiveSitePlans: [],
  },
  reducers: {
    trackLoadedSitePlans: (state, action) => {
      const existingIndex = state.loadedInteractiveSitePlans.findIndex(
        (item) =>
          item.communityId === action.payload.communityId
      );
    
      if (existingIndex !== -1) {
        state.loadedInteractiveSitePlans[existingIndex] = {
          ...state.loadedInteractiveSitePlans[existingIndex],
          ...action.payload,
        };
      } else {
        state.loadedInteractiveSitePlans.push(action.payload);
      }
    },
    untrackInteractiveSitePlans: (state) => {
      state.loadedInteractiveSitePlans = [];
    },
    setInteractiveSitePlan: (state, action) => {
      const existingIndex = state.interactiveSitePlans.findIndex(
        (item) =>
          item.communityId === action.payload.communityId &&
          item.sitePlanId === action.payload.sitePlanId
      );
    
      if (existingIndex !== -1) {
        state.interactiveSitePlans[existingIndex] = {
          ...state.interactiveSitePlans[existingIndex],
          ...action.payload,
        };
      } else {
        state.interactiveSitePlans.push(action.payload);
      }
    },
    removeInteractiveSitePlan: (state, action) => {
      state.interactiveSitePlans = state.interactiveSitePlans.filter(
        sitePlan => sitePlan.sitePlanId !== action.payload
      );
    },
    setLotInformation: (state, action) => {
      state.lotId = action.payload.lotId;
      state.communityId = action.payload.communityId;
      state.lotNumber = action.payload.lotNumber;
      state.availableSpec = action.payload.availableSpec;
      state.availablePlans = action.payload.availablePlans;
      state.interactiveSitePlanCardStatus = action.payload.interactiveSitePlanCardStatus;
      state.lotStatus = action.payload.lotStatus;
      state.sitePlanId = action.payload.sitePlanId;
    },
    removeLotInformation: (state) => {
      state.lotId = null;
      state.communityId = null;
      state.lotNumber = null;
      state.availableSpec = null;
      state.availablePlans = null;
      state.interactiveSitePlanCard = null;
      state.lotStatus = null;
      state.sitePlanId = null;
      state.interactiveSitePlanCardStatus = LoadingStatus.Idle.toString();
    },
    getInteractiveSitePlanCardFulfilled: (state, action) => {
      if (state.interactiveSitePlanCardStatus === LoadingStatus.Loading.toString()) {
        state.interactiveSitePlanCardStatus = LoadingStatus.Succeeded.toString();
        state.interactiveSitePlanCard = action.payload;
      }
    },
    getInteractiveSitePlanCardFailed: (state) => {
      state.interactiveSitePlanCardStatus = LoadingStatus.Failed.toString();
      state.interactiveSitePlanCard = null;
    },
    clearInteractiveSitePlanCard: (state) => {
      state.interactiveSitePlanCardStatus = LoadingStatus.Idle.toString();
      state.interactiveSitePlanCard = null;
    },
  }
});